import React, { useState, useEffect } from 'react';
import { MasterView } from './views/MasterView';
import { ClientView } from './views/ClientView';
import { GlassCard, GlassButton, GlassInput, ScreenContainer } from './components/GlassUI';
import { UserRole, User } from './types';
import { MOCK_MASTER, MOCK_CLIENTS } from './constants';

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Simulate checking for a logged-in user on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      const savedUser = localStorage.getItem('glowbook_user');
      if (savedUser) {
        setUser(JSON.parse(savedUser));
      }
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleLogin = (role: UserRole) => {
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      const loggedInUser = role === 'MASTER' ? MOCK_MASTER : MOCK_CLIENTS[0];
      setUser(loggedInUser);
      localStorage.setItem('glowbook_user', JSON.stringify(loggedInUser));
      setIsLoading(false);
    }, 800);
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('glowbook_user');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-100 via-purple-100 to-pink-100">
        <div className="animate-pulse flex flex-col items-center">
           <div className="w-16 h-16 bg-violet-600/20 backdrop-blur-xl rounded-2xl flex items-center justify-center mb-4 border border-white/40 shadow-xl">
             <div className="w-8 h-8 bg-violet-600 rounded-lg animate-spin" style={{ animationDuration: '3s' }}></div>
           </div>
           <h1 className="text-2xl font-bold text-slate-800">GlowBook</h1>
        </div>
      </div>
    );
  }

  if (!user) {
    return <AuthScreen onLogin={handleLogin} />;
  }

  return user.role === 'MASTER' ? (
    <MasterView onLogout={handleLogout} />
  ) : (
    <ClientView onLogout={handleLogout} currentUser={user} />
  );
}

const AuthScreen: React.FC<{ onLogin: (role: UserRole) => void }> = ({ onLogin }) => {
  return (
    <ScreenContainer className="flex flex-col justify-center h-screen">
      <div className="mb-10 text-center">
        <div className="w-20 h-20 bg-gradient-to-tr from-violet-500 to-fuchsia-500 rounded-3xl mx-auto mb-6 shadow-xl shadow-violet-200 flex items-center justify-center text-white text-4xl font-bold">
          G
        </div>
        <h1 className="text-3xl font-bold text-slate-800 mb-2">Добро пожаловать</h1>
        <p className="text-slate-500">Современная CRM для вашего бизнеса</p>
      </div>

      <GlassCard className="space-y-6">
        <div className="space-y-2">
          <label className="text-sm font-bold text-slate-700 ml-1">Войти как</label>
          <div className="grid grid-cols-2 gap-4">
             {/* Note: In a real app, these would be separate forms. For demo, buttons suffice. */}
            <GlassButton variant="secondary" onClick={() => onLogin('CLIENT')}>
               Клиент
            </GlassButton>
            <GlassButton variant="primary" onClick={() => onLogin('MASTER')}>
               Мастер
            </GlassButton>
          </div>
        </div>
        
        <div className="relative flex py-2 items-center">
            <div className="flex-grow border-t border-slate-200"></div>
            <span className="flex-shrink-0 mx-4 text-slate-400 text-xs">ДЕМО ДОСТУП</span>
            <div className="flex-grow border-t border-slate-200"></div>
        </div>

        <form className="space-y-4 opacity-50 pointer-events-none" onSubmit={(e) => e.preventDefault()}>
            <GlassInput type="email" placeholder="Email" />
            <GlassInput type="password" placeholder="Пароль" />
            <GlassButton fullWidth>Войти</GlassButton>
        </form>
      </GlassCard>
      
      <p className="text-center text-slate-400 text-sm mt-8">
        Нет аккаунта? <span className="font-bold text-violet-600">Регистрация</span>
      </p>
    </ScreenContainer>
  );
};

export default App;